import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  Check,
  ChevronLeft,
  Dumbbell,
  HeartPulse,
  Home,
  Loader2,
  MessageCircle,
  NotebookTabs,
  Salad,
  Sparkles,
  WalletCards,
} from 'lucide-react';
import { useProduct } from '../context/ProductContext';
import { BossMark } from './shell/BossLogo';
import { isProductWalkthroughComplete } from '../lib/productWalkthrough';
import type { ProductConfig, ProductEdition, ProductId } from '../lib/api';
import hosHomePavilion from '../assets/hos-home-pavilion.png';
import entranceElevatorScene from '../assets/entrance-elevator-scene.png';

interface MeResponse {
  /** Legacy API field retained for mixed-version upgrades. */
  onboardingWizardComplete?: boolean;
  /** Server-computed, per-installed-product walkthrough requirement. */
  needsProductWalkthrough?: boolean;
  role: string;
}

const MODULE_DETAILS: Record<string, { label: string; note: string; icon: typeof Home }> = {
  assistant: { label: 'Personal Assistant', note: 'Your private home command center and conversation space.', icon: Bot },
  social: { label: 'Social Media', note: 'Platform tabs, embedded views when allowed, and safe pop-outs.', icon: MessageCircle },
  'address-book': { label: 'Address Book', note: 'Personal contacts, relationships, birthdays, and connected sources.', icon: NotebookTabs },
  health: { label: 'HOS Health', note: 'Your self-hosted health hub and connected-device record.', icon: HeartPulse },
  fitness: { label: 'Fitness Coach', note: 'Plan workouts, schedule sessions, and compare plan with activity.', icon: Dumbbell },
  nutrition: { label: 'Nutrition Coach', note: 'Keep food goals and nutrition plans without invented measurements.', icon: Salad },
  finances: { label: 'Financial Coach', note: 'Personal goals and planning kept separate from business finance.', icon: WalletCards },
  spiritual: { label: 'Spiritual Space', note: 'A private, belief-neutral place for reflection and practice.', icon: Sparkles },
};

function token(): string {
  try { return localStorage.getItem('boss_token') ?? ''; } catch { return ''; }
}

async function getMe(): Promise<MeResponse> {
  const response = await fetch('/api/auth/me', {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!response.ok) throw new Error(response.status === 401 ? 'Your session expired. Please sign in again.' : 'Could not check first-login status.');
  return response.json() as Promise<MeResponse>;
}

async function completeWizard(): Promise<void> {
  const response = await fetch('/api/auth/complete-wizard', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!response.ok) throw new Error('Could not complete the walkthrough.');
}

export function personalizedIdentityInitial(value: string, placeholders: readonly string[]): string {
  const clean = value.trim().replace(/\s+/g, ' ');
  const normalized = clean.toLowerCase();
  return placeholders.some((placeholder) => placeholder.toLowerCase() === normalized) ? '' : clean;
}

type FirstRunIdentity = Pick<ProductConfig, 'bosName' | 'hosName' | 'eaName' | 'paName'>;

/**
 * Combined installations normally enforce surface-local identity writes. The
 * owner walkthrough first enters setup mode so one mandatory step can name
 * both installed products, then the API restores that boundary on completion.
 */
export function firstRunIdentityPatches(
  edition: ProductEdition,
  identity: FirstRunIdentity,
): Array<Partial<ProductConfig>> {
  const names = {
    ...(edition !== 'hos' ? { bosName: identity.bosName, eaName: identity.eaName } : {}),
    ...(edition !== 'bos' ? { hosName: identity.hosName, paName: identity.paName } : {}),
  };
  return edition === 'both'
    ? [{ configured: false }, names]
    : [{ ...names, configured: false }];
}

export function firstRunAudience(
  edition: ProductEdition,
  defaultProduct: ProductId,
  canConfigure: boolean,
) {
  const hasBos = edition === 'bos' || edition === 'both';
  const hasHos = (edition === 'hos' || edition === 'both') && canConfigure;
  const isTeamSeat = !canConfigure && hasBos;
  const isCombinedTeamSeat = isTeamSeat && edition === 'both';
  const editionTitle = isTeamSeat
    ? 'BOS team workspace'
    : edition === 'both' ? 'BOS + HOS' : edition.toUpperCase();
  const headerEyebrow = isTeamSeat
    ? 'Private BOS team seat'
    : edition === 'both'
      ? 'Private BOS + HOS installation'
      : `Private ${edition.toUpperCase()} installation`;
  const roomsDescription = isTeamSeat
    ? isCombinedTeamSeat
      ? 'The left rail moves between the business rooms enabled for your seat. HOS and system administration remain owner-only.'
      : 'The left rail moves between the business rooms enabled for your seat. System administration and owner settings remain owner-only.'
    : edition === 'both'
      ? 'The left rail moves between rooms. The BOS/HOS switch changes sides without mixing business and personal context. Settings is where you finish any requested OAuth or device connections.'
      : edition === 'bos'
        ? 'The left rail moves between business rooms. Settings is where you finish any requested integrations.'
        : 'The left rail moves between home rooms. Settings is where you finish any requested integrations or device connections.';
  const bosRoomsDescription = !hasBos
    ? ''
    : isTeamSeat
      ? isCombinedTeamSeat
        ? 'Your BOS team seat opens the business rooms your installation owner enabled. Personal HOS data and system administration are not part of this seat.'
        : 'Your BOS team seat opens the business rooms your installation owner enabled. System administration and owner integration settings are not part of this seat.'
    : edition === 'both'
      ? 'Your existing BOS remains the business side: Dashboard, Office, Advisory Board, task planning, Canvas, CRM, LinkedIn, WhatsApp, agents, and business settings. Health now lives only on the HOS side.'
      : 'Your BOS includes Dashboard, Office, Advisory Board, task planning, Canvas, CRM, LinkedIn, WhatsApp, agents, and business settings, with every room scoped to business operations.';

  return {
    hasBos,
    hasHos,
    isTeamSeat,
    editionTitle,
    headerEyebrow,
    welcomeDescription: isTeamSeat
      ? 'This private business workspace runs on your organization\'s self-hosted BOS. We will show you its identity and the rooms available to your seat.'
      : 'This instance runs for you on its own server and Postgres database. We will name the spaces and assistants already included, then show you where everything lives.',
    roomsDescription,
    bosRoomsDescription,
    destination: isTeamSeat ? '/' : defaultProduct === 'hos' ? '/hos' : '/',
    entryProduct: isTeamSeat ? 'BOS' : defaultProduct.toUpperCase(),
  };
}

export function FirstRunExperience({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { config, loading: productLoading, updateConfig } = useProduct();
  const [checking, setChecking] = useState(true);
  const [complete, setComplete] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bosName, setBosName] = useState('');
  const [hosName, setHosName] = useState('');
  const [eaName, setEaName] = useState('');
  const [paName, setPaName] = useState('');
  const [canConfigure, setCanConfigure] = useState(false);

  const audience = firstRunAudience(config.edition, config.defaultProduct, canConfigure);
  const { hasBos, hasHos } = audience;

  useEffect(() => {
    setBosName(personalizedIdentityInitial(config.bosName, ['BOS', 'Business Operating System']));
    setHosName(personalizedIdentityInitial(config.hosName, ['HOS', 'Home Operating System']));
    setEaName(personalizedIdentityInitial(config.eaName, ['Executive Assistant']));
    setPaName(personalizedIdentityInitial(config.paName, ['Personal Assistant']));
  }, [config.bosName, config.eaName, config.hosName, config.paName]);

  const check = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const me = await getMe();
      setComplete(isProductWalkthroughComplete(me));
      setCanConfigure(me.role === 'admin' || me.role === 'owner');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load the walkthrough.');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { void check(); }, [check]);

  const installedModules = useMemo(
    () => {
      if (!hasHos) return [];
      const ids = ['assistant', ...config.modules.filter((id) => id !== 'assistant')];
      return ids.flatMap((id) => {
        const detail = MODULE_DETAILS[id];
        return detail ? [{ id, ...detail }] : [];
      });
    },
    [config.modules, hasHos],
  );

  const identityValid = !canConfigure || ((!hasBos || (!!bosName.trim() && !!eaName.trim()))
    && (!hasHos || (!!hosName.trim() && !!paName.trim())));

  async function saveIdentity() {
    if (!identityValid || saving) return;
    if (!canConfigure) {
      setError(null);
      setStep(2);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const identity = {
        bosName: bosName.trim(),
        hosName: hosName.trim(),
        eaName: eaName.trim(),
        paName: paName.trim(),
      };
      for (const patch of firstRunIdentityPatches(config.edition, identity)) {
        await updateConfig(patch);
      }
      setStep(2);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save your names.');
    } finally {
      setSaving(false);
    }
  }

  async function finish() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      if (canConfigure) await updateConfig({ configured: true });
      await completeWizard();
      setComplete(true);
      navigate(audience.destination, { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not finish the walkthrough.');
    } finally {
      setSaving(false);
    }
  }

  if (complete) return <>{children}</>;

  if (checking || productLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#10120d] text-[#f8f2e7]">
        <div className="flex items-center gap-3 text-sm"><Loader2 className="h-5 w-5 animate-spin" /> Preparing your private workspace…</div>
      </div>
    );
  }

  if (error && step === 0) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#10120d] p-6 text-[#f8f2e7]">
        <div className="w-full max-w-md rounded-2xl border border-white/15 bg-black/55 p-6 text-center backdrop-blur-xl">
          <h1 className="text-xl font-semibold">The walkthrough could not start</h1>
          <p className="mt-3 text-sm text-white/65">{error}</p>
          <div className="mt-6 flex justify-center gap-3">
            <button className="rounded-lg border border-white/20 px-4 py-2 text-sm" onClick={() => navigate('/login', { replace: true })}>Sign in again</button>
            <button className="rounded-lg bg-[#c9a66b] px-4 py-2 text-sm font-semibold text-[#17120a]" onClick={() => void check()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const background = hasHos ? hosHomePavilion : entranceElevatorScene;
  const { editionTitle } = audience;

  return (
    <div className="relative min-h-screen overflow-auto bg-[#10120d] p-5 text-[#f8f2e7] sm:p-10">
      <img src={background} alt="" className="fixed inset-0 h-full w-full object-cover" aria-hidden />
      <div className="fixed inset-0 bg-[linear-gradient(115deg,rgba(14,15,10,.92),rgba(24,20,13,.64),rgba(12,13,9,.86))]" aria-hidden />

      <main className="relative mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center">
        <section className="w-full overflow-hidden rounded-[28px] border border-white/15 bg-[#16170f]/80 shadow-2xl backdrop-blur-2xl">
          <header className="flex items-center gap-4 border-b border-white/10 px-6 py-5 sm:px-9">
            <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2">
              <BossMark scale={0.82} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[.3em] text-[#d7c49e]">{audience.headerEyebrow}</div>
              <div className="mt-1 text-lg font-semibold">{editionTitle} first-login walkthrough</div>
            </div>
            <div className="ml-auto hidden items-center gap-2 sm:flex" aria-label={`Step ${step + 1} of 3`}>
              {[0, 1, 2].map((item) => <span key={item} className={`h-1.5 rounded-full ${item === step ? 'w-8 bg-[#d6b574]' : item < step ? 'w-4 bg-[#76947a]' : 'w-4 bg-white/15'}`} />)}
            </div>
          </header>

          <div className="p-6 sm:p-9">
            {step === 0 && (
              <div className="grid gap-8 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
                <div>
                  <div className="text-xs font-medium uppercase tracking-[.25em] text-[#d6b574]">Your system, your data</div>
                  <h1 className="mt-4 max-w-2xl text-4xl font-semibold leading-tight sm:text-5xl">Welcome home to your {editionTitle}.</h1>
                  <p className="mt-5 max-w-xl text-base leading-7 text-white/70">{audience.welcomeDescription}</p>
                  <button className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#d6b574] px-5 py-3 text-sm font-semibold text-[#19140c]" onClick={() => setStep(1)}>
                    {canConfigure ? 'Personalize my system' : 'Start walkthrough'} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="rounded-3xl border border-white/15 bg-black/25 p-6">
                  <div className="text-xs uppercase tracking-[.24em] text-white/50">Installed offering</div>
                  <div className="mt-4 text-3xl font-semibold">{editionTitle}</div>
                  <ul className="mt-5 space-y-3 text-sm text-white/65">
                    {hasBos && <li className="flex gap-3"><Check className="mt-0.5 h-4 w-4 text-[#8fb193]" /> Business command center and Executive Assistant</li>}
                    {hasHos && <li className="flex gap-3"><Check className="mt-0.5 h-4 w-4 text-[#8fb193]" /> Personal home system and Personal Assistant</li>}
                    <li className="flex gap-3"><Check className="mt-0.5 h-4 w-4 text-[#8fb193]" /> Private, self-hosted data and integrations</li>
                  </ul>
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <div className="max-w-2xl">
                  <div className="text-xs font-medium uppercase tracking-[.25em] text-[#d6b574]">Identity</div>
                  <h1 className="mt-3 text-3xl font-semibold">{canConfigure ? 'Name your system and assistants' : 'Meet your system and assistants'}</h1>
                  <p className="mt-3 text-sm leading-6 text-white/60">{canConfigure ? 'These names live on this server and appear throughout the lockup, navigation, assistant room, and conversation surfaces.' : 'Your installation owner chose these names. They appear throughout the lockup, navigation, assistant room, and conversation surfaces.'}</p>
                </div>
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  {hasBos && (
                    <div className="rounded-2xl border border-white/15 bg-black/25 p-5">
                      <div className="text-sm font-semibold">Business Operating System</div>
                      {canConfigure ? (
                        <>
                          <label className="mt-5 block text-xs text-white/55" htmlFor="first-bos-name">Chosen BOS name</label>
                          <input id="first-bos-name" value={bosName} onChange={(event) => setBosName(event.target.value)} maxLength={80} placeholder="e.g. Northstar" className="mt-2 w-full rounded-xl border border-white/15 bg-black/30 px-4 py-3 text-sm outline-none focus:border-[#d6b574]" />
                          <label className="mt-4 block text-xs text-white/55" htmlFor="first-ea-name">Executive Assistant name</label>
                          <input id="first-ea-name" value={eaName} onChange={(event) => setEaName(event.target.value)} maxLength={80} placeholder="e.g. Gio" className="mt-2 w-full rounded-xl border border-white/15 bg-black/30 px-4 py-3 text-sm outline-none focus:border-[#d6b574]" />
                        </>
                      ) : (
                        <dl className="mt-5 space-y-4 rounded-xl border border-white/10 bg-black/20 p-4">
                          <div><dt className="text-xs text-white/50">BOS name</dt><dd className="mt-1 text-sm">{config.bosName || 'BOS'}</dd></div>
                          <div><dt className="text-xs text-white/50">Executive Assistant</dt><dd className="mt-1 text-sm">{config.eaName || 'Executive Assistant'}</dd></div>
                        </dl>
                      )}
                      <div className="mt-5 overflow-hidden rounded-xl bg-white/5 px-4 py-3">
                        <BossMark product="bos" productName={bosName.trim() || 'BOS'} scale={0.82} />
                      </div>
                    </div>
                  )}
                  {hasHos && (
                    <div className="rounded-2xl border border-[#8fa77a]/30 bg-[#61705a]/10 p-5">
                      <div className="text-sm font-semibold">Home Operating System</div>
                      {canConfigure ? (
                        <>
                          <label className="mt-5 block text-xs text-white/55" htmlFor="first-hos-name">Chosen HOS name</label>
                          <input id="first-hos-name" value={hosName} onChange={(event) => setHosName(event.target.value)} maxLength={80} placeholder="e.g. Hearth" className="mt-2 w-full rounded-xl border border-white/15 bg-black/30 px-4 py-3 text-sm outline-none focus:border-[#9fb487]" />
                          <label className="mt-4 block text-xs text-white/55" htmlFor="first-pa-name">Personal Assistant name</label>
                          <input id="first-pa-name" value={paName} onChange={(event) => setPaName(event.target.value)} maxLength={80} placeholder="e.g. Layla" className="mt-2 w-full rounded-xl border border-white/15 bg-black/30 px-4 py-3 text-sm outline-none focus:border-[#9fb487]" />
                        </>
                      ) : (
                        <dl className="mt-5 space-y-4 rounded-xl border border-white/10 bg-black/20 p-4">
                          <div><dt className="text-xs text-white/50">HOS name</dt><dd className="mt-1 text-sm">{config.hosName || 'HOS'}</dd></div>
                          <div><dt className="text-xs text-white/50">Personal Assistant</dt><dd className="mt-1 text-sm">{config.paName || 'Personal Assistant'}</dd></div>
                        </dl>
                      )}
                      <div className="mt-5 overflow-hidden rounded-xl bg-white/5 px-4 py-3">
                        <BossMark product="hos" productName={hosName.trim() || 'HOS'} scale={0.82} />
                      </div>
                    </div>
                  )}
                </div>
                {error && <p role="alert" className="mt-5 text-sm text-[#f4a6a6]">{error}</p>}
                <div className="mt-8 flex items-center justify-between">
                  <button className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white/60" onClick={() => setStep(0)}><ChevronLeft className="h-4 w-4" /> Back</button>
                  <button disabled={!identityValid || saving} className="inline-flex items-center gap-2 rounded-xl bg-[#d6b574] px-5 py-3 text-sm font-semibold text-[#19140c] disabled:cursor-not-allowed disabled:opacity-40" onClick={() => void saveIdentity()}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {canConfigure ? 'Save names' : 'Continue'} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="max-w-2xl">
                  <div className="text-xs font-medium uppercase tracking-[.25em] text-[#d6b574]">Your rooms</div>
                  <h1 className="mt-3 text-3xl font-semibold">Here is what your installer included</h1>
                  <p className="mt-3 text-sm leading-6 text-white/60">{audience.roomsDescription}</p>
                </div>
                {hasHos && installedModules.length > 0 && (
                  <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {installedModules.map(({ id, label, note, icon: Icon }) => (
                      <div key={id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                        <Icon className="h-5 w-5 text-[#a9bd91]" />
                        <div className="mt-3 text-sm font-semibold">{label}</div>
                        <p className="mt-1 text-xs leading-5 text-white/50">{note}</p>
                      </div>
                    ))}
                  </div>
                )}
                {hasBos && (
                  <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 p-5 text-sm text-white/60">{audience.bosRoomsDescription}</div>
                )}
                {error && <p role="alert" className="mt-5 text-sm text-[#f4a6a6]">{error}</p>}
                <div className="mt-8 flex items-center justify-between">
                  <button className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white/60" onClick={() => setStep(1)}><ChevronLeft className="h-4 w-4" /> Back</button>
                  <button disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#d6b574] px-5 py-3 text-sm font-semibold text-[#19140c] disabled:opacity-50" onClick={() => void finish()}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enter my {audience.entryProduct}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
